import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, ArrowDown, Minus, Circle, Lightning, Pulse, CursorClick, ChartBar, Plugs, Plus, Trash, X, PlayCircle, CheckCircle as Check } from '@phosphor-icons/react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/* ── Constants ── */
var FEATURE_LABELS = {
  'F-001': 'API Templates',
  'F-002': 'Notifications',
  'F-003': 'Transforms',
  'F-004': 'NL Interface',
  'F-005': 'Schema Monitor',
  'F-006': 'Query Engine',
  'F-007': 'Environment Map',
};

var FEATURE_ORDER = ['F-001', 'F-002', 'F-003', 'F-004', 'F-005', 'F-006', 'F-007'];

var HEALTH_COLORS = { green: '#1D9E75', amber: '#C8A030', red: '#E24B4A' };

function formatTs(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-US', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

/* ── Trend arrow ── */
function TrendArrow({ trend, pct }) {
  if (trend === 'up') return <span className="flex items-center gap-0.5 text-kova-green text-[10px] font-mono"><ArrowUp size={10} weight="bold" />{pct > 0 ? '+' + pct + '%' : ''}</span>;
  if (trend === 'down') return <span className="flex items-center gap-0.5 text-kova-red text-[10px] font-mono"><ArrowDown size={10} weight="bold" />{pct}%</span>;
  return <span className="flex items-center gap-0.5 text-kova-slate text-[10px] font-mono"><Minus size={10} />flat</span>;
}

/* ── Event rate bar ── */
function RateBar({ feature, data, maxRate }) {
  var rate = data.rate_per_hour;
  var barWidth = maxRate > 0 ? Math.max(2, (rate / maxRate) * 100) : 2;
  var has24h = data.events_last_24h > 0;

  return (
    <div className="flex items-center gap-3 group" data-testid={'rate-bar-' + feature}>
      <div className="w-[72px] shrink-0">
        <div className="text-[10px] font-mono text-kova-silv">{feature}</div>
        <div className="text-[8px] font-mono text-kova-slate truncate">{FEATURE_LABELS[feature]}</div>
      </div>
      <div className="flex-1 h-5 relative">
        <div className="absolute inset-0 bg-kova-cyan/4 rounded-sm" />
        <motion.div
          className="absolute left-0 top-0 h-full rounded-sm"
          style={{ background: has24h ? '#0ABFA3' : 'rgba(10,191,163,0.12)' }}
          initial={{ width: 0 }}
          animate={{ width: barWidth + '%' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
        <div className="absolute right-2 top-0 h-full flex items-center">
          <span className="text-[9px] font-mono text-kova-silv">{rate}/h</span>
        </div>
      </div>
      <div className="w-[60px] text-right shrink-0">
        <div className="text-[10px] font-mono text-kova-silv">{data.events_last_24h}</div>
        <div className="text-[7px] font-mono text-kova-slate">24h</div>
      </div>
      <div className="w-[50px] text-right shrink-0">
        <div className="text-[10px] font-mono text-kova-slate">{data.events_last_7d}</div>
        <div className="text-[7px] font-mono text-kova-slate">7d</div>
      </div>
    </div>
  );
}

/* ── Live event feed ── */
function LiveFeed({ events }) {
  return (
    <div className="divide-y divide-kova-cyan/6 max-h-[280px] overflow-y-auto" data-testid="live-feed">
      {events.length === 0 ? (
        <div className="p-4 text-center text-xs font-mono text-kova-slate">No events yet</div>
      ) : events.map(function (ev, i) {
        var typeColor = '#0ABFA3';
        if (ev.event_type.includes('fail') || ev.event_type.includes('error')) typeColor = '#E24B4A';
        else if (ev.event_type.includes('click') || ev.event_type.includes('viewed')) typeColor = '#D4A574';
        var summary = Object.entries(ev.payload_summary || {}).map(function (e) { return e[0] + ':' + e[1]; }).join(' ');
        return (
          <motion.div key={ev.event_id || i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }} className="flex items-center gap-3 px-3 py-1.5 hover:bg-white/[0.02] transition-colors" data-testid={'feed-event-' + i}>
            <Circle size={6} weight="fill" style={{ color: typeColor }} className="shrink-0" />
            <span className="text-[10px] font-mono" style={{ color: typeColor }}>{ev.event_type}</span>
            {summary && <span className="text-[9px] font-mono text-kova-slate truncate flex-1">{summary}</span>}
            <span className="text-[9px] font-mono text-kova-slate shrink-0">{formatTs(ev.timestamp)}</span>
            <span className={'text-[8px] font-mono px-1 ' + (ev.status === 'delivered' ? 'text-kova-green' : ev.status === 'failed' ? 'text-kova-red' : 'text-kova-slate')}>{ev.status}</span>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ── Suggestion heatmap ── */
function SuggestionHeatmap({ data }) {
  var topSuggestions = data.top_suggestions || [];
  var crossPairs = data.cross_system_pairs || [];
  var maxClicks = topSuggestions.length > 0 ? topSuggestions[0].clicks : 1;

  return (
    <div className="space-y-4" data-testid="suggestion-heatmap">
      {/* Cross-system pair interest */}
      {crossPairs.length > 0 && (
        <div>
          <div className="text-[9px] font-mono text-kova-slate uppercase tracking-[2px] mb-2">Cross-system interest</div>
          <div className="flex flex-wrap gap-2">
            {crossPairs.map(function (cp) {
              var intensity = Math.min(1, cp.clicks / Math.max(1, maxClicks));
              return (
                <div key={cp.pair} className="px-3 py-2 border transition-colors" style={{ borderColor: 'rgba(10,191,163,' + (0.15 + intensity * 0.6) + ')', background: 'rgba(10,191,163,' + (intensity * 0.08) + ')' }} data-testid={'cross-pair-' + cp.pair.replace(/\s/g, '-')}>
                  <div className="text-[11px] font-mono text-kova-cyan">{cp.pair}</div>
                  <div className="text-[9px] font-mono text-kova-slate mt-0.5">{cp.clicks} click{cp.clicks !== 1 ? 's' : ''}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top clicked suggestions */}
      <div>
        <div className="text-[9px] font-mono text-kova-slate uppercase tracking-[2px] mb-2">Most clicked suggestions</div>
        {topSuggestions.length === 0 ? (
          <p className="text-xs font-mono text-kova-slate">No suggestion clicks recorded yet</p>
        ) : (
          <div className="space-y-1">
            {topSuggestions.map(function (s, i) {
              var intensity = s.clicks / maxClicks;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={'top-suggestion-' + i}>
                  <div className="flex-1 relative h-7">
                    <div className="absolute inset-0 bg-kova-cyan/3" />
                    <motion.div className="absolute left-0 top-0 h-full" style={{ background: 'rgba(10,191,163,' + (0.1 + intensity * 0.2) + ')' }} initial={{ width: 0 }} animate={{ width: (intensity * 100) + '%' }} transition={{ duration: 0.5, delay: i * 0.05 }} />
                    <div className="absolute inset-0 flex items-center px-2">
                      <span className="text-[10px] font-mono text-kova-silv truncate">{s.text}</span>
                    </div>
                  </div>
                  <span className="text-[10px] font-mono text-kova-cyan w-8 text-right shrink-0">{s.clicks}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-6 pt-2 border-t border-kova-cyan/12">
        <div>
          <div className="text-[18px] font-heading font-bold text-kova-cyan">{data.total_clicks}</div>
          <div className="text-[8px] font-mono text-kova-slate uppercase">Total clicks</div>
        </div>
        <div>
          <div className="text-[18px] font-heading font-bold text-kova-silv">{Math.round(data.click_rate * 100)}%</div>
          <div className="text-[8px] font-mono text-kova-slate uppercase">Click rate</div>
        </div>
        <div>
          <div className="text-[18px] font-heading font-bold text-kova-gold">{crossPairs.length}</div>
          <div className="text-[8px] font-mono text-kova-slate uppercase">Cross pairs</div>
        </div>
      </div>
    </div>
  );
}

/* ── Feature adoption card ── */
function AdoptionCard({ label, value, sub, metric }) {
  return (
    <div className="card-brutal p-4 space-y-1" data-testid={'adoption-' + label.toLowerCase().replace(/\s/g, '-')}>
      <div className="text-[9px] font-mono text-kova-slate uppercase tracking-wider">{label}</div>
      <div className="flex items-end justify-between">
        <span className="text-2xl font-heading font-bold text-white">{value}</span>
        {metric && metric.trend && <TrendArrow trend={metric.trend} pct={metric.change_pct} />}
      </div>
      {sub && <div className="text-[9px] font-mono text-kova-slate">{sub}</div>}
    </div>
  );
}

/* ── Delivery Endpoints Manager ── */
function DeliveryEndpoints() {
  var [endpoints, setEndpoints] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showAdd, setShowAdd] = useState(false);
  var [newUrl, setNewUrl] = useState('');
  var [newLabel, setNewLabel] = useState('');
  var [newAuth, setNewAuth] = useState('');

  useEffect(function () {
    fetch(API + '/matr/endpoints', { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : []; })
      .then(setEndpoints)
      .catch(function () {})
      .finally(function () { setLoading(false); });
  }, []);

  function addEndpoint() {
    if (!newUrl.trim()) return;
    fetch(API + '/matr/endpoints', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ url: newUrl, label: newLabel || newUrl.slice(0, 40), auth_header: newAuth }),
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (ep) {
        if (ep) { setEndpoints(function (prev) { return [ep].concat(prev); }); setShowAdd(false); setNewUrl(''); setNewLabel(''); setNewAuth(''); toast.success('Endpoint added'); }
      })
      .catch(function () { toast.error('Failed to add endpoint'); });
  }

  function deleteEndpoint(id) {
    fetch(API + '/matr/endpoints/' + id, { method: 'DELETE', credentials: 'include' })
      .then(function (r) { if (r.ok) { setEndpoints(function (prev) { return prev.filter(function (e) { return e.endpoint_id !== id; }); }); toast.success('Removed'); } })
      .catch(function () {});
  }

  function toggleEndpoint(id, active) {
    fetch(API + '/matr/endpoints/' + id, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ is_active: !active }),
    })
      .then(function (r) {
        if (r.ok) { setEndpoints(function (prev) { return prev.map(function (e) { return e.endpoint_id === id ? Object.assign({}, e, { is_active: !active }) : e; }); }); }
      })
      .catch(function () {});
  }

  function testEndpoint(id) {
    fetch(API + '/matr/endpoints/' + id + '/test', { method: 'POST', credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (d && d.success) toast.success('Ping successful (HTTP ' + d.status_code + ')');
        else toast.error('Ping failed: ' + (d ? d.error || 'HTTP ' + d.status_code : 'Unknown'));
      })
      .catch(function () { toast.error('Test failed'); });
  }

  return (
    <div className="card-brutal overflow-hidden" data-testid="delivery-endpoints-section">
      <div className="px-4 py-3 border-b border-kova-cyan/12 flex items-center justify-between bg-kova-card">
        <div className="flex items-center gap-2">
          <Plugs size={14} className="text-kova-cyan" />
          <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Delivery Endpoints</span>
        </div>
        <Button onClick={function () { setShowAdd(!showAdd); }} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] uppercase h-6 px-2" data-testid="add-endpoint-btn">
          <Plus size={10} className="mr-1" />Add
        </Button>
      </div>
      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="p-4 space-y-2 border-b border-kova-cyan/12 bg-kova-bg" data-testid="add-endpoint-form">
              <input value={newLabel} onChange={function (e) { setNewLabel(e.target.value); }} placeholder="Label (optional)" className="w-full bg-kova-card border border-kova-cyan/12 text-xs font-mono text-white px-3 py-1.5 focus:outline-none focus:border-kova-cyan/50" />
              <input value={newUrl} onChange={function (e) { setNewUrl(e.target.value); }} placeholder="https://your-webhook.example.com/events" className="w-full bg-kova-card border border-kova-cyan/12 text-xs font-mono text-white px-3 py-1.5 focus:outline-none focus:border-kova-cyan/50" data-testid="endpoint-url-input" />
              <input value={newAuth} onChange={function (e) { setNewAuth(e.target.value); }} placeholder="Authorization header (optional)" className="w-full bg-kova-card border border-kova-cyan/12 text-xs font-mono text-white px-3 py-1.5 focus:outline-none focus:border-kova-cyan/50" />
              <div className="flex gap-2">
                <Button onClick={addEndpoint} size="sm" className="bg-kova-cyan text-kova-bg hover:bg-kova-cyan/85 rounded-none font-mono text-[9px] uppercase" data-testid="save-endpoint-btn">Save</Button>
                <Button onClick={function () { setShowAdd(false); }} size="sm" variant="outline" className="rounded-none border-kova-cyan/12 font-mono text-[9px] uppercase">Cancel</Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="divide-y divide-kova-cyan/6">
        {loading ? (
          <div className="p-4 text-center text-xs font-mono text-kova-slate">Loading endpoints...</div>
        ) : endpoints.length === 0 ? (
          <div className="p-6 text-center text-xs font-mono text-kova-slate">No delivery endpoints configured. Add one to receive MATR events externally.</div>
        ) : endpoints.map(function (ep) {
          return (
            <div key={ep.endpoint_id} className="px-4 py-3 flex items-center gap-3" data-testid={'endpoint-' + ep.endpoint_id}>
              <span className={'w-2 h-2 rounded-full shrink-0 ' + (ep.is_active ? 'bg-kova-green' : 'bg-kova-slate')} />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-mono text-kova-silv truncate">{ep.label}</div>
                <div className="text-[9px] font-mono text-kova-slate truncate">{ep.url}</div>
              </div>
              <button onClick={function () { testEndpoint(ep.endpoint_id); }} className="text-kova-slate hover:text-kova-cyan transition-colors" title="Test ping"><PlayCircle size={16} /></button>
              <button onClick={function () { toggleEndpoint(ep.endpoint_id, ep.is_active); }} className={'text-[9px] font-mono px-2 py-0.5 border transition-colors ' + (ep.is_active ? 'border-kova-green/30 text-kova-green' : 'border-kova-cyan/12 text-kova-slate')}>{ep.is_active ? 'On' : 'Off'}</button>
              <button onClick={function () { deleteEndpoint(ep.endpoint_id); }} className="text-kova-slate hover:text-kova-red transition-colors"><Trash size={14} /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════
   MAIN MATR DASHBOARD
   ═══════════════════════════════ */
export default function MatrDashboard() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [autoRefresh, setAutoRefresh] = useState(true);

  var fetchData = useCallback(function () {
    fetch(API + '/matr/analytics', { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { if (d) setData(d); })
      .catch(function () { toast.error('Failed to load MATR analytics'); })
      .finally(function () { setLoading(false); });
  }, []);

  useEffect(function () { fetchData(); }, [fetchData]);

  // Auto-refresh every 30s
  useEffect(function () {
    if (!autoRefresh) return;
    var interval = setInterval(fetchData, 30000);
    return function () { clearInterval(interval); };
  }, [autoRefresh, fetchData]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><span className="font-mono text-kova-cyan animate-pulse">LOADING MATR...</span></div>;
  }

  if (!data) {
    return <div className="text-center py-12 text-kova-slate font-mono">Failed to load analytics</div>;
  }

  var flow = data.event_flow;
  var pipeline = flow.pipeline;
  var suggestions = data.suggestion_intelligence;
  var adoption = data.feature_adoption;
  var maxRate = Math.max.apply(null, FEATURE_ORDER.map(function (f) { return (flow.by_feature[f] || {}).rate_per_hour || 0; }).concat([0.1]));
  var healthColor = HEALTH_COLORS[pipeline.health] || HEALTH_COLORS.amber;

  return (
    <div className="space-y-6" data-testid="matr-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-kova-cyan/60 text-lg" title="MATR Intelligence Layer">&#9672;</span>
          <div>
            <h1 className="font-heading font-bold text-lg">MATR Analytics</h1>
            <p className="text-[10px] font-mono text-kova-slate mt-0.5">Intelligence layer event pipeline</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={function () { setAutoRefresh(!autoRefresh); }}
            className={'text-[9px] font-mono px-2 py-1 border transition-colors ' + (autoRefresh ? 'border-kova-cyan/30 text-kova-cyan bg-kova-cyan/10' : 'border-kova-cyan/12 text-kova-slate')}
            data-testid="auto-refresh-toggle"
          >
            {autoRefresh ? 'Live' : 'Paused'}
          </button>
          <button onClick={fetchData} className="text-[9px] font-mono text-kova-slate hover:text-kova-silv px-2 py-1 border border-kova-cyan/12 transition-colors" data-testid="refresh-analytics-btn">Refresh</button>
        </div>
      </div>

      {/* ═══ SECTION 1: Event Flow Rates ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="card-brutal overflow-hidden">
          <div className="px-4 py-3 border-b border-kova-cyan/12 flex items-center justify-between bg-kova-card">
            <div className="flex items-center gap-2">
              <Pulse size={14} className="text-kova-cyan" weight="bold" />
              <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Event Flow by Feature</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: healthColor }} />
              <span className="text-[9px] font-mono" style={{ color: healthColor }}>{pipeline.health}</span>
            </div>
          </div>
          <div className="p-4 space-y-2">
            {FEATURE_ORDER.map(function (feat) {
              var fdata = flow.by_feature[feat] || { rate_per_hour: 0, events_last_24h: 0, events_last_7d: 0 };
              return <RateBar key={feat} feature={feat} data={fdata} maxRate={maxRate} />;
            })}
          </div>
          {/* Pipeline stats footer */}
          <div className="px-4 py-2.5 border-t border-kova-cyan/12 flex items-center gap-6 bg-kova-bg">
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-mono text-kova-slate">Total:</span>
              <span className="text-[11px] font-mono text-white">{pipeline.total}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-kova-green" />
              <span className="text-[9px] font-mono text-kova-slate">{pipeline.delivered} delivered</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-kova-gold" />
              <span className="text-[9px] font-mono text-kova-slate">{pipeline.queued} queued</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-kova-red" />
              <span className="text-[9px] font-mono text-kova-slate">{pipeline.failed} failed</span>
            </div>
            <span className="text-[9px] font-mono text-kova-slate ml-auto">{Math.round(pipeline.delivery_rate * 100)}% delivery</span>
          </div>
        </div>

        {/* Live feed */}
        <div className="card-brutal overflow-hidden">
          <div className="px-4 py-3 border-b border-kova-cyan/12 flex items-center gap-2 bg-kova-card">
            <Lightning size={14} className="text-kova-cyan" weight="fill" />
            <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Live Feed</span>
            {autoRefresh && <span className="w-1.5 h-1.5 rounded-full bg-kova-cyan animate-pulse ml-auto" />}
          </div>
          <LiveFeed events={flow.live_feed} />
        </div>
      </div>

      {/* ═══ SECTION 2: Suggestion Click Intelligence ═══ */}
      <div className="card-brutal overflow-hidden">
        <div className="px-4 py-3 border-b border-kova-cyan/12 flex items-center gap-2 bg-kova-card">
          <CursorClick size={14} className="text-kova-gold" />
          <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Suggestion Intelligence</span>
          <span className="text-[8px] font-mono text-kova-slate ml-auto">Intent data from query suggestions</span>
        </div>
        <div className="p-4">
          <SuggestionHeatmap data={suggestions} />
        </div>
      </div>

      {/* ═══ SECTION 3: Feature Adoption Overview ═══ */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ChartBar size={14} className="text-kova-purple" />
          <span className="text-[10px] font-mono text-kova-slate uppercase tracking-[2px]">Feature Adoption</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <AdoptionCard label="Connections" value={adoption.connections.active} sub={adoption.connections.total + ' total'} />
          <AdoptionCard label="Jobs" value={adoption.jobs.active} sub={adoption.jobs.total + ' total'} />
          <AdoptionCard label="Executions" value={adoption.executions.current} metric={adoption.executions} sub="this week" />
          <AdoptionCard label="Queries" value={adoption.queries.current} metric={adoption.queries} sub="this week" />
          <AdoptionCard label="NL Sessions" value={adoption.nl_sessions.current} metric={adoption.nl_sessions} sub="this week" />
          <AdoptionCard label="Transforms" value={adoption.transforms.total} sub="rules configured" />
          <AdoptionCard label="Schema Monitors" value={adoption.schema_monitors.enabled} sub={adoption.schema_monitors.total + ' configured'} />
          <AdoptionCard label="Map Views" value={adoption.map_views.current} metric={adoption.map_views} sub="this week" />
          <AdoptionCard label="Impact Analyses" value={adoption.impact_analyses.current} metric={adoption.impact_analyses} sub="this week" />
        </div>
      </div>

      {/* ═══ SECTION 4: Delivery Endpoints ═══ */}
      <DeliveryEndpoints />
    </div>
  );
}
